import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { matchesDropdownSearch } from "./dropdownSearchMatch";
import { createPortal } from "react-dom";

// Text input with a chevron that lists previously entered venues. Typing keeps
// working as free text; picking a row hands the whole venue to the caller.
// Claude Design (8) ds-components/VenueCombo.jsx.
export type VenueComboOption = {
  name: string;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  postal?: string | null;
  country?: string | null;
};

const venueDetailKeys = ["address", "city", "province", "postal", "country"] as const;

// Claude Design (8) Create Event Screen.dc.html:2418-2444: skip venues with no
// name, keep one row per name (case-insensitive) and fill its missing parts
// from later duplicates, then sort A to Z.
export const buildVenueComboOptions = (
  venues: readonly VenueComboOption[],
): VenueComboOption[] => {
  const byName = new Map<string, VenueComboOption>();
  for (const venue of venues) {
    const name = (venue.name || "").trim();
    if (!name) continue;
    const key = name.toLocaleLowerCase();
    const existing = byName.get(key);
    if (!existing) {
      byName.set(key, { ...venue, name });
      continue;
    }
    for (const detail of venueDetailKeys) {
      if (!existing[detail] && venue[detail]) existing[detail] = venue[detail];
    }
  }
  return Array.from(byName.values()).sort((left, right) =>
    left.name.localeCompare(right.name),
  );
};

type VenueComboProps = {
  value: string;
  options: readonly VenueComboOption[];
  onText: (value: string) => void;
  onPick: (venue: VenueComboOption) => void;
  error?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
};

type PanelPosition = {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

const panelMaxHeight = 260;

export const VenueCombo = ({
  value,
  options,
  onText,
  onPick,
  error,
  placeholder = "Venue Name",
  ariaLabel = "Venue Name",
  id,
}: VenueComboProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const list = options.filter((option) => option.name);
  const shown = query
    ? list.filter((option) => matchesDropdownSearch(option.name, query))
    : list;

  // SearchableSelectField pattern: the list is position:fixed and anchored to
  // the field, so section cards never clip it. It opens upward near the
  // bottom of the window.
  const place = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const useAbove = below < 160 && above > below;
    setPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
      width: Math.min(rect.width, window.innerWidth - 16),
      maxHeight: Math.max(0, Math.min(panelMaxHeight, useAbove ? above : below)),
      ...(useAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  const openList = (filter: string) => {
    place();
    setQuery(filter);
    setActiveIndex(-1);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (
        !rootRef.current?.contains(event.target as Node) &&
        !panelRef.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", outside);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  const pick = (venue: VenueComboOption) => {
    onPick(venue);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openList("");
        return;
      }
      if (!shown.length) return;
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const next =
        activeIndex < 0
          ? direction > 0 ? 0 : shown.length - 1
          : (activeIndex + direction + shown.length) % shown.length;
      setActiveIndex(next);
      panelRef.current
        ?.querySelector(`[data-option-index="${next}"]`)
        ?.scrollIntoView?.({ block: "nearest" });
    }
    // Enter only picks a highlighted row, so a typed new venue stays as typed.
    if (event.key === "Enter" && open && shown[activeIndex]) {
      event.preventDefault();
      pick(shown[activeIndex]);
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
    if (event.key === "Tab") setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative w-full min-w-0">
      <input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && shown[activeIndex] ? `${listId}-option-${activeIndex}` : undefined
        }
        aria-invalid={error || undefined}
        value={value || ""}
        placeholder={placeholder}
        title={value || ""}
        onChange={(event) => {
          onText(event.target.value);
          openList(event.target.value);
        }}
        onFocus={() => {
          if (list.length) openList("");
        }}
        onKeyDown={onKeyDown}
        className="block w-full truncate rounded-[8px] border border-[#9E9E9E] bg-white py-[10px] pl-[12px] pr-[40px] text-[14px] leading-[20px] text-[#0A0A0A] outline-none placeholder:text-[#9E9E9E] focus:border-[#E1A731]"
        style={error ? { borderColor: "#F0202E" } : undefined}
      />
      <button
        type="button"
        aria-label="Show previous venues"
        tabIndex={-1}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => (open ? setOpen(false) : openList(""))}
        className="absolute right-[6px] top-1/2 flex h-[28px] w-[28px] -translate-y-1/2 items-center justify-center border-0 bg-transparent p-0"
      >
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#727272"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            data-venue-pop="1"
            className="fixed z-[11000] overflow-y-auto rounded-[8px] border border-[#9E9E9E] bg-white p-[4px] text-[#0A0A0A]"
            style={{
              ...position,
              boxShadow: "0 8px 24px rgba(0,0,0,.12)",
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {shown.length === 0 ? (
              <div className="px-[12px] py-[10px] text-[13px] text-[#727272]">
                {list.length
                  ? "No matching venues. Keep typing to add a new venue."
                  : "No previous venues yet."}
              </div>
            ) : (
              shown.map((venue, index) => {
                const selected = venue.name === value;
                const details = [venue.address, venue.city, venue.province]
                  .filter(Boolean)
                  .join(", ");
                return (
                  <button
                    key={venue.name}
                    id={`${listId}-option-${index}`}
                    data-option-index={index}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    title={venue.name}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setActiveIndex(index)}
                    onClick={() => pick(venue)}
                    className={`flex w-full flex-col items-start gap-[2px] rounded-[6px] border-0 px-[10px] py-[8px] text-left ${
                      selected
                        ? "bg-[#FDF6E7]"
                        : index === activeIndex
                          ? "bg-[#F7F7F8]"
                          : "bg-transparent hover:bg-[#F7F7F8]"
                    }`}
                  >
                    <span className="max-w-full truncate text-[14px] font-semibold text-[#0A0A0A]">
                      {venue.name}
                    </span>
                    {venue.address || venue.city ? (
                      <span className="max-w-full truncate text-[12px] text-[#727272]">
                        {details}
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default VenueCombo;
